import { Check, Pencil, Timer, TimerOff, Trash2, X } from "lucide-react";
import * as React from "react";

import { Button, ButtonGroup, Textarea } from "@/components/ui";
import { cn } from "@/lib/utils";

import {
  clampQaTimestampMs,
  formatQaTimestamp,
  LastFailResolution,
  nudgeQaTimestampMs,
  parseQaTimestamp,
  QA_TIMESTAMP_NUDGE_MS,
} from "./qaIssues";

function isPlainEnter(event: React.KeyboardEvent) {
  return event.key === "Enter" && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey;
}

export function FlaggedIssueCard({
  label,
  comment,
  timestampMs,
  durationSeconds,
  focused,
  needsResolution,
  readOnly,
  canDelete,
  resolution,
  issueId,
  cardRef,
  autoFocusComment,
  onSelect,
  onChangeTimestamp,
  onUsePlayhead,
  onChangeComment,
  onDeselect,
  onDelete,
  onResolved,
  onUnresolved,
}: {
  label: string;
  comment: string;
  timestampMs?: number | null;
  durationSeconds: number;
  focused: boolean;
  needsResolution?: boolean;
  readOnly?: boolean;
  canDelete?: boolean;
  resolution?: LastFailResolution;
  issueId?: string;
  cardRef?: React.Ref<HTMLDivElement>;
  autoFocusComment?: boolean;
  onSelect: () => void;
  onChangeTimestamp: (timestampMs: number | null) => void;
  onUsePlayhead: () => void;
  onChangeComment: (comment: string) => void;
  onDeselect?: () => void;
  onDelete?: () => void;
  onResolved?: () => void;
  onUnresolved?: () => void;
}) {
  const [commentLeft, setCommentLeft] = React.useState(false);
  const [commentFocused, setCommentFocused] = React.useState(false);
  const commentReadOnly = Boolean(readOnly || resolution === "RESOLVED");
  const showCommentError = !commentReadOnly && commentLeft && !comment.trim();
  const commentNodeRef = React.useRef<HTMLTextAreaElement | null>(null);

  React.useEffect(() => {
    if (!focused || commentReadOnly) setCommentFocused(false);
  }, [focused, commentReadOnly]);

  React.useEffect(() => {
    if (focused && autoFocusComment && !commentReadOnly) setCommentFocused(true);
  }, [autoFocusComment, commentReadOnly, focused]);

  React.useEffect(() => {
    if (commentFocused) commentNodeRef.current?.focus();
  }, [commentFocused]);

  const openComment = () => {
    if (commentReadOnly) return;
    setCommentFocused(true);
  };

  const blurComment = (textarea: HTMLTextAreaElement) => {
    textarea.closest<HTMLElement>("[data-qa-flag-card]")?.focus();
  };

  return (
    <div
      ref={cardRef}
      tabIndex={0}
      data-qa-flag-card=""
      data-qa-issue-id={issueId}
      className={cn(
        "group cursor-pointer overflow-hidden rounded-lg border text-xs outline-none",
        needsResolution
          ? "border-red-400 bg-red-50"
          : focused
          ? "border-amber-400 bg-amber-50"
          : "border-slate-200 bg-white hover:border-amber-400 hover:bg-amber-50"
      )}
      onFocus={event => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        onSelect();
      }}
      onKeyDown={event => {
        if (!isPlainEnter(event)) {
          return;
        }
        if (
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLInputElement ||
          event.target instanceof HTMLButtonElement
        ) {
          return;
        }
        if (commentReadOnly) return;
        event.preventDefault();
        event.stopPropagation();
        openComment();
      }}
    >
      <div
        className={cn(
          "flex min-h-7 items-center gap-1 rounded-t-lg border-b px-2 py-1",
          needsResolution
            ? "border-red-200 bg-red-100/80"
            : focused
            ? "border-amber-200 bg-amber-100/70"
            : "border-slate-200 bg-slate-50 group-hover:border-amber-200 group-hover:bg-amber-100/70"
        )}
      >
        <span title={label} className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
          {label}
        </span>
        {readOnly && timestampMs == null ? null : (
          <FlagTimestampControls
            timestampMs={timestampMs}
            durationSeconds={durationSeconds}
            readOnly={readOnly}
            onSeek={onSelect}
            onChange={onChangeTimestamp}
            onUsePlayhead={onUsePlayhead}
          />
        )}
        {canDelete ? (
          <button
            type="button"
            title="Remove issue (Backspace)"
            aria-label="Remove issue (Backspace)"
            className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded text-slate-400 hover:bg-white hover:text-red-500"
            onClick={event => {
              event.stopPropagation();
              onDelete?.();
            }}
          >
            <Trash2 className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="px-2 py-1.5">
        {commentReadOnly || !commentFocused ? (
          commentReadOnly && !comment ? null : (
            <CommentPreview comment={comment} readOnly={commentReadOnly} onEdit={openComment} />
          )
        ) : null}
        {commentFocused && !commentReadOnly ? (
          <Textarea
            ref={commentNodeRef}
            autoSize
            minRows={2}
            maxRows={4}
            value={comment}
            placeholder="What should be fixed?"
            className="mt-1.5"
            onClick={event => event.stopPropagation()}
            onChange={event => onChangeComment(event.target.value)}
            onBlur={() => {
              setCommentFocused(false);
              setCommentLeft(true);
            }}
            onKeyDown={event => {
              if (!isPlainEnter(event)) {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              blurComment(event.currentTarget);
            }}
          />
        ) : null}
        {showCommentError ? <p className="mt-1 text-[11px] font-medium text-red-600">Add a comment</p> : null}
        {readOnly || !onResolved || !onUnresolved ? null : (
          <ButtonGroup className="mt-1.5" onClick={event => event.stopPropagation()}>
            <Button
              type="button"
              size="xs"
              variant={resolution === "RESOLVED" ? "default" : "outline"}
              className={cn(
                "h-6 px-2 text-[11px]",
                resolution === "RESOLVED" && "bg-emerald-500 text-white hover:bg-emerald-400",
                needsResolution && resolution !== "RESOLVED" && "border-red-300"
              )}
              onClick={() => {
                onResolved();
                onDeselect?.();
              }}
            >
              <Check className="size-3" />
              Resolved
            </Button>
            <Button
              type="button"
              size="xs"
              variant={resolution === "UNRESOLVED" ? "default" : "outline"}
              className={cn(
                "h-6 px-2 text-[11px]",
                resolution === "UNRESOLVED" && "bg-rose-500 text-white hover:bg-rose-400",
                needsResolution && resolution !== "UNRESOLVED" && "border-red-300"
              )}
              onClick={() => {
                onUnresolved();
              }}
            >
              <X className="size-3" />
              Not resolved
            </Button>
          </ButtonGroup>
        )}
      </div>
    </div>
  );
}

function CommentPreview({ comment, readOnly, onEdit }: { comment: string; readOnly?: boolean; onEdit: () => void }) {
  if (readOnly) {
    return <p className="text-left text-slate-500">{comment}</p>;
  }

  return (
    <button
      type="button"
      tabIndex={-1}
      title="Edit comment"
      className="flex w-full cursor-text items-start gap-1.5 text-left text-slate-500 hover:text-slate-700"
      onClick={event => {
        event.stopPropagation();
        onEdit();
      }}
    >
      <span className={cn("min-w-0 flex-1", !comment.trim() && "italic")}>
        {comment.trim() || "What should be fixed?"}
      </span>
      <Pencil className="mt-0.5 size-3 shrink-0" />
    </button>
  );
}

function FlagTimestampControls({
  timestampMs,
  durationSeconds,
  readOnly,
  onSeek,
  onChange,
  onUsePlayhead,
}: {
  timestampMs?: number | null;
  durationSeconds: number;
  readOnly?: boolean;
  onSeek: () => void;
  onChange: (timestampMs: number | null) => void;
  onUsePlayhead: () => void;
}) {
  const display = formatQaTimestamp(timestampMs) || "0:00";
  const [draft, setDraft] = React.useState(display);

  React.useEffect(() => {
    setDraft(display);
  }, [display]);

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed || /^(whole|all)(\s+cut)?$/i.test(trimmed)) {
      onChange(null);
      return;
    }
    const parsed = parseQaTimestamp(draft);
    if (parsed == null) {
      setDraft(display);
      return;
    }
    onChange(clampQaTimestampMs(parsed, durationSeconds));
  };

  const iconButtonClass =
    "flex size-6 shrink-0 cursor-pointer items-center justify-center overflow-visible rounded leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700";

  if (timestampMs == null) {
    if (readOnly) return null;
    return (
      <button
        type="button"
        aria-label="Add timestamp at playhead"
        title="Add timestamp at playhead"
        className={cn("shrink-0", iconButtonClass)}
        onClick={event => {
          event.stopPropagation();
          onUsePlayhead();
        }}
      >
        <Timer className="size-3.5 shrink-0" />
      </button>
    );
  }

  if (readOnly) {
    return (
      <button
        type="button"
        title="Jump to this time"
        className="flex h-7 shrink-0 cursor-pointer items-center leading-none text-slate-400"
        onClick={onSeek}
      >
        {display}
      </button>
    );
  }

  return (
    <div className="flex h-7 shrink-0 items-center gap-0.5 overflow-visible" onClick={event => event.stopPropagation()}>
      <input
        value={draft}
        aria-label="Flag timestamp"
        title="Issue timestamp. Up/down adjusts by 1 second"
        tabIndex={-1}
        className="h-6 w-10 rounded border border-transparent bg-transparent px-0.5 text-center text-[11px] leading-none font-medium text-slate-500 outline-none hover:border-slate-200 focus:border-slate-300 focus:text-slate-800"
        onFocus={onSeek}
        onChange={event => setDraft(event.target.value)}
        onBlur={commitDraft}
        onKeyDown={event => {
          if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            const delta = event.key === "ArrowUp" ? QA_TIMESTAMP_NUDGE_MS : -QA_TIMESTAMP_NUDGE_MS;
            onChange(nudgeQaTimestampMs(parseQaTimestamp(draft) ?? timestampMs, delta, durationSeconds));
            return;
          }
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
            return;
          }
          event.stopPropagation();
        }}
      />
      <button
        type="button"
        aria-label="Remove timestamp"
        title="Remove timestamp"
        className={iconButtonClass}
        onClick={event => {
          event.stopPropagation();
          onChange(null);
        }}
      >
        <TimerOff className="size-3.5 shrink-0" />
      </button>
    </div>
  );
}

export function FlagTimelineMarker({
  title,
  leftPercent,
  selected,
  readOnly,
  onSelect,
  onSeek,
  onMove,
}: {
  title: string;
  leftPercent: number;
  selected: boolean;
  readOnly?: boolean;
  onSelect: () => void;
  onSeek: () => void;
  onMove: (clientX: number) => void;
}) {
  const movedRef = React.useRef(false);

  return (
    <button
      type="button"
      data-qa-flag-marker=""
      title={readOnly ? title : `${title} · drag to adjust`}
      className={cn(
        "absolute top-1/2 z-10 flex size-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center",
        readOnly ? "cursor-pointer" : "cursor-grab active:cursor-grabbing"
      )}
      style={{ left: `${leftPercent}%` }}
      onClick={event => event.stopPropagation()}
      onPointerDown={event => {
        event.stopPropagation();
        if (readOnly) return;
        movedRef.current = false;
        onSelect();
        event.currentTarget.setPointerCapture(event.pointerId);
      }}
      onPointerMove={event => {
        if (readOnly || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
        if (Math.abs(event.movementX) > 0 || Math.abs(event.movementY) > 0) movedRef.current = true;
        if (movedRef.current) onMove(event.clientX);
      }}
      onPointerUp={event => {
        event.stopPropagation();
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        if (!movedRef.current) {
          onSelect();
          onSeek();
        }
      }}
    >
      <span className={cn("size-2.5 rounded-full", selected ? "bg-amber-300 ring-2 ring-amber-200" : "bg-amber-500")} />
    </button>
  );
}
